"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Phone, Clock, ShieldCheck, Zap, MapPin, Calendar } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function UrgentCall() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="urgentcalltoaction" data-nav="light" className="relative bg-background py-8 sm:py-11 md:py-14 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-8 md:p-10 text-center shadow-lg">
          {/* Subtle background decorative badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/10 px-3.5 py-1.5 text-xs font-semibold text-secondary mb-6">
            <Zap className="size-3.5" />
            <span>Fast same-day service in Los Angeles</span>
          </div>

          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground max-w-2xl mx-auto leading-tight">
            Need tires today?
          </h2>

          {/* Subtext */}
          <p className="mt-4 text-base sm:text-lg text-foreground/80 max-w-xl mx-auto leading-relaxed">
            Call to check current availability and wait time, or stop by — walk-ins are welcome.
          </p>

          {/* CTA Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto h-14 px-8 text-lg font-bold shadow-md hover:shadow-lg transition-[color,background-color,border-color,box-shadow]">
              <a href="tel:+14243138474" className="flex items-center justify-center gap-3">
                <Phone className="size-5 shrink-0" />
                <span>Call (424) 313-8474</span>
              </a>
            </Button>

            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-lg font-bold border-2 border-foreground/20 text-foreground hover:border-primary hover:text-primary hover:bg-accent/30 shadow-md transition-[color,background-color,border-color,box-shadow]">
              <Link to="https://www.google.com/maps/dir/?api=1&destination=8635+W+Pico+Blvd+Los+Angeles+CA+90035" newTab className="flex items-center justify-center gap-3">
                <MapPin className="size-5 shrink-0 text-primary" />
                <span>Get Directions</span>
              </Link>
            </Button>
          </div>

          {/* Value props strip */}
          <div className="mt-6 pt-6 border-t border-border/80 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs sm:text-sm text-foreground/75">
            <div data-index={0} className="flex items-center justify-center gap-2">
              <Clock className="size-4 text-secondary shrink-0" />
              <span>No appointment needed</span>
            </div>
            <div data-index={1} className="flex items-center justify-center gap-2">
              <SafeHydrate><Calendar className="size-4 text-secondary shrink-0" /></SafeHydrate>
              <span>Open 7 Days</span>
            </div>
            <div data-index={2} className="flex items-center justify-center gap-2">
              <ShieldCheck className="size-4 text-secondary shrink-0" />
              <span>Free Visual Inspection</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}